import { createRouter, createWebHistory } from 'vue-router';

import LandingView from '@/views/LandingView.vue';
import LoginView from '@/views/LoginView.vue';
import NotFoundView from '@/views/NotFoundView.vue';

const AdminHealthView = () => import('@/views/admin/HealthView.vue');
const AdminAppsView = () => import('@/views/admin/AppsListView.vue');
const AdminClientsView = () => import('@/views/admin/ClientsListView.vue');
const AdminApiKeyRotateView = () => import('@/views/admin/ApiKeyRotateView.vue');
const AdminAuditView = () => import('@/views/admin/AuditView.vue');
const AdminUsersListView = () => import('@/views/admin/UsersListView.vue');
const AdminManualPasswordResetView = () => import('@/views/admin/ManualPasswordResetView.vue');
const BrowserUnsupportedView = () => import('@/views/BrowserUnsupportedView.vue');
const UserProfileView = () => import('@/views/UserProfileView.vue');
// Story 6.10 — heatmap drill-down. Lazy-loaded because it pulls in the
// screenshot/overlay components and isn't on the landing critical path.
const HeatmapView = () => import('@/views/HeatmapView.vue');
// Visor de documentación del repo (Markdown → HTML). Lazy: arrastra
// `marked`, que no debe pesar en el bundle crítico del landing.
const DocsView = () => import('@/views/DocsView.vue');
// T001-3 — portal de cliente. Lazy: el rol interno nunca lo carga.
const PortalView = () => import('@/views/PortalView.vue');
const ProbeInventoryView = () => import('@/views/ProbeInventoryView.vue');
const ProbeLogsView = () => import('@/views/ProbeLogsView.vue');
const ProbeHeartbeatView = () => import('@/views/ProbeHeartbeatView.vue');
const PasswordResetConfirmView = () => import('@/views/PasswordResetConfirmView.vue');
const PasswordResetRequestView = () => import('@/views/PasswordResetRequestView.vue');
const PasswordResetExpiredView = () => import('@/views/PasswordResetExpiredView.vue');
const PasswordResetUnavailableView = () => import('@/views/PasswordResetUnavailableView.vue');

const baseRoutes = [
    { path: '/', name: 'landing', component: LandingView, meta: { requiresAuth: true } },
    { path: '/login', name: 'login', component: LoginView, meta: { shell: false, public: true } },
    { path: '/browser-unsupported', name: 'browser-unsupported', component: BrowserUnsupportedView, meta: { shell: false, public: true } },
    // Portal de cliente (T001-3): sin chrome de Otera (`shell: false`).
    { path: '/portal', name: 'portal', component: PortalView, meta: { shell: false, requiresAuth: true } },
    { path: '/probes', name: 'probe-inventory', component: ProbeInventoryView, meta: { requiresAuth: true } },
    { path: '/probes/logs', name: 'probe-logs', component: ProbeLogsView, meta: { requiresAuth: true } },
    { path: '/probes/heartbeat', name: 'probe-heartbeat', component: ProbeHeartbeatView, meta: { requiresAuth: true } },
    { path: '/admin/health', name: 'admin-health', component: AdminHealthView, meta: { adminOnly: true, requiresAuth: true } },
    // Crear / editar app son modales sobre la lista (Fase 7): deep-link
    // vía /admin/apps?create=1 y ?edit=<appId>.
    { path: '/admin/apps', name: 'admin-apps', component: AdminAppsView, meta: { adminOnly: true, requiresAuth: true } },
    { path: '/admin/apps/:appId/rotate-key', name: 'admin-app-rotate-key', component: AdminApiKeyRotateView, meta: { adminOnly: true, requiresAuth: true } },
    { path: '/admin/apps/:appId/rotate-server-key', name: 'admin-app-rotate-server-key', component: AdminApiKeyRotateView, meta: { adminOnly: true, requiresAuth: true } },
    // T001-4 — clientes (`tenants`): dueños de las apps y de los usuarios.
    { path: '/admin/tenants', name: 'admin-tenants', component: AdminClientsView, meta: { adminOnly: true, requiresAuth: true } },
    { path: '/admin/audit', name: 'admin-audit', component: AdminAuditView, meta: { adminOnly: true, requiresAuth: true } },
    // Story 6.10 — heatmap drill-down. Param pageUrlHash is a 16 hex
    // char SHA-256 truncation matching the collector's screenshot key.
    { path: '/heatmap/:pageUrlHash', name: 'heatmap', component: HeatmapView, meta: { requiresAuth: true } },
    // Visor de documentación in-app — `:doc` resuelto contra whitelist.
    { path: '/guide/:doc', name: 'guide', component: DocsView, meta: { requiresAuth: true } },
    // Story 5.6 — admin users + manual password reset.
    { path: '/admin/users', name: 'admin-users', component: AdminUsersListView, meta: { adminOnly: true, requiresAuth: true } },
    { path: '/admin/users/:userId/reset-password', name: 'admin-user-reset-password', component: AdminManualPasswordResetView, meta: { adminOnly: true, requiresAuth: true } },
    // Perfil propio — cualquier usuario logueado (no admin-gated).
    { path: '/profile', name: 'profile', component: UserProfileView, meta: { requiresAuth: true } },
    // Password reset (Epic 5) — públicas, shell-less. El router gate
    // (Story 5.4) verifica smtpEnabled antes de servir estas rutas.
    { path: '/password-reset/request', name: 'password-reset-request', component: PasswordResetRequestView, meta: { shell: false, public: true, smtpGated: true } },
    { path: '/reset-password', name: 'password-reset-confirm', component: PasswordResetConfirmView, meta: { shell: false, public: true, smtpGated: true } },
    { path: '/reset-password/expired', name: 'password-reset-expired', component: PasswordResetExpiredView, meta: { shell: false, public: true, smtpGated: true } },
    { path: '/reset-password/unavailable', name: 'password-reset-unavailable', component: PasswordResetUnavailableView, meta: { shell: false, public: true } }
];

// Catch-all 404 — rendered inside AppShell so it picks up the chrome.
const notFoundRoute = { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView, meta: { requiresAuth: true } };

// /__ds — visor del manual de diseño "Otera". Solo en desarrollo:
// `import.meta.env.DEV` es `false` en `vite build`, así que el bloque es
// código muerto y Rollup elimina el chunk lazy y todo su grafo (@ds/*).
// En producción la ruta no existe y el catch-all 404 cubre /__ds.
// Excepción puntual a la regla single-source de env: se necesita el literal
// estático para que la eliminación de código muerto funcione.
 
// Epic 2 Wave 1A — operator triage views (Stories 2.4, 2.5, 2.6).
// Lazy because they pull in explore composables not on the landing path.
const ErrorListView    = () => import('@/views/ErrorListView.vue');
const ExplorerView     = () => import('@/views/ExplorerView.vue');
const SessionDetailView = () => import('@/views/SessionDetailView.vue');
const SyntheticResultsView = () => import('@/views/SyntheticResultsView.vue');
baseRoutes.push(
  { path: '/apps/:appId/errors',                 name: 'apps-errors',  component: ErrorListView,    meta: { requiresAuth: true } },
  { path: '/apps/:appId/explore',                name: 'apps-explore', component: ExplorerView,     meta: { requiresAuth: true } },
  { path: '/apps/:appId/sessions/:sessionId',    name: 'apps-session', component: SessionDetailView, meta: { requiresAuth: true } },
  { path: '/apps/:appId/e2e',                    name: 'apps-e2e',     component: SyntheticResultsView, meta: { requiresAuth: true } },
);

const devRoutes = import.meta.env.DEV
  ? [{
      path: '/__ds',
      name: 'design-system',
      component: () => import('@/views/DesignSystemView.vue'),
      meta: { shell: false, public: true }
    }]
  : [];

export const router = createRouter({
  history: createWebHistory(),
  routes: [...baseRoutes, ...devRoutes, notFoundRoute]
});

export function installAuthGuard(router, authStore) {
  router.beforeEach((to) => {
    // Story 5.4 AC2 — rutas smtpGated redirigen a Unavailable cuando
    // SMTP no está configurado en este deployment. El gate se evalúa
    // ANTES del auth gate porque estas rutas son públicas (`public:true`).
    // Si el config aún no se cargó (smtpEnabled === null), dejamos pasar
    // — la vista hará su propio handling. El default seguro es
    // "asumir SMTP no configurado" (auth store inicializa así si fetch falla).
    if (to.meta?.smtpGated && authStore.config.smtpEnabled === false) {
      return { name: 'password-reset-unavailable' };
    }
    if (to.meta?.public) return true;
    if (!to.meta?.requiresAuth) return true;
    if (authStore.isAuthenticated) {
      // El rol tenant_client solo tiene portal y perfil: el backend le
      // niega los paneles internos (requireInternalUser), así que
      // dejarlo entrar al landing solo le mostraría 403s.
      if (authStore.role === 'tenant_client'
          && to.name !== 'portal' && to.name !== 'profile') {
        return { name: 'portal' };
      }
      if (to.meta.adminOnly && !authStore.isAdmin) return { path: '/' };
      return true;
    }
    return { path: '/login', query: { return_to: to.fullPath } };
  });
}
